import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { useStatsigClient } from '@statsig/react-bindings';
import { GATES } from '@constants';
import { getNarrativeIcon, toast } from '@utilities';
import { createNarrative, readNarrativeType } from '@api/narratives';

import {
  getPromptPillTooltip,
  getPromptPillValue,
  transformPromptSelections,
} from '../../utilities/helpers';

import PreBuildModal from '../../../../components/PreBuildModal';
import { filterPrompts, transformPromptAnswers } from './utilities/helpers';

const getAllModalPrompts = (prompts) =>
  prompts.flatMap((prompt) => [prompt, ...(prompt?.groupedPrompts || [])]);

const ModalBuildNarrative = ({
  flowId,
  onNarrativeBuilt,
  route,
  selectedPrompt,
  setIsModalOpen,
  topicIds = null,
}) => {
  const [activePrompt, setActivePrompt] = useState(null);
  const [buildError, setBuildError] = useState(null);
  const [narrativeTitle, setNarrativeTitle] = useState('');
  const [isBuilding, setIsBuilding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalPrompts, setModalPrompts] = useState([]);
  const [nonPromptAnswers, setNonPromptAnswers] = useState({});
  const [searchPrompts, setSearchPrompts] = useState([]);
  const [sameLevelPrompts, setSameLevelPrompts] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [transformedAnswersMap, setTransformedAnswersMap] = useState({});

  const { client } = useStatsigClient();
  const shouldApplyPromptGrouping = client.checkGate(GATES.NARRATIVE_SHOPPER_ATTRIBUTES_PROMPT);

  const navigate = useNavigate();
  const abortControllerRef = useRef(null);
  const normalizedTopicIds = useMemo(() => {
    if (!Array.isArray(topicIds)) return [];
    return topicIds.filter(
      (topicId) => topicId != null && (typeof topicId !== 'string' || topicId.trim().length > 0)
    );
  }, [topicIds]);
  const dynamicTopicIds = useMemo(() => {
    if (route?.id !== 'dynamic') return null;
    return normalizedTopicIds.length > 0 ? normalizedTopicIds : null;
  }, [normalizedTopicIds, route?.id]);
  const dynamicTopicIdsSignature = useMemo(
    () => JSON.stringify(dynamicTopicIds || []),
    [dynamicTopicIds]
  );
  const normalizedRouteName = route?.name || route?.id || 'Narrative';

  const normalizePrompt = useCallback((prompt = {}) => {
    const fallbackName =
      prompt?.title || prompt?.shortTitle || prompt?.promptKey || prompt?.id || '';
    return {
      ...prompt,
      name: prompt?.name || fallbackName,
    };
  }, []);

  const processAnswers = ({ answers, defaultAnswers, modalPrompts }) => {
    let repromptAnswers;
    if (answers && Object.keys(answers).length > 0) {
      const { subscribedCategories, subscribedChannels, ...rest } = answers;
      repromptAnswers = {
        subscribed_categories: subscribedCategories,
        subscribed_channel: subscribedChannels,
        ...rest,
      };
    }
    // Use answers if available, otherwise fallback to defaultAnswers
    const answersToProcess = repromptAnswers || defaultAnswers;

    // Transform date_range if needed
    if (answersToProcess?.date_range && !answersToProcess.date_range.date_range) {
      answersToProcess.date_range = { date_range: answersToProcess.date_range };
    }

    const newTransformedAnswersMap = {};
    const defaultNonPromptAnswers = {};
    const allModalPrompts = getAllModalPrompts(modalPrompts);

    Object.keys(answersToProcess).forEach((promptKey) => {
      const answers = answersToProcess[promptKey];
      const prompt = allModalPrompts.find((p) => p.promptKey === promptKey);

      if (prompt) {
        const transformed = transformPromptSelections({ answers, prompt });
        newTransformedAnswersMap[promptKey] = transformed;
      } else {
        defaultNonPromptAnswers[promptKey] = answersToProcess[promptKey];
      }
    });

    return {
      nonPromptAnswers: defaultNonPromptAnswers,
      selectedAnswers: answersToProcess,
      transformedAnswersMap: newTransformedAnswersMap,
    };
  };

  useEffect(() => {
    const fetchNarrative = async () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const parsedDynamicTopicIds =
        dynamicTopicIdsSignature === '[]' ? null : JSON.parse(dynamicTopicIdsSignature);

      setIsLoading(true);
      try {
        const data = await readNarrativeType({
          flowId: parsedDynamicTopicIds ? null : flowId,
          narrativeId: route?.id,
          signal: abortControllerRef.current?.signal,
          topicIds: parsedDynamicTopicIds,
        });

        const grouping = shouldApplyPromptGrouping ? data?.promptGrouping : null;

        const {
          answers = {},
          defaultAnswers = {},
          hierarchySearchPrompts = data?.searchPrompts || [],
          modalPrompts = filterPrompts(data.prompts, grouping).map(normalizePrompt),
          sameLevelPrompts = [],
        } = data;

        const { nonPromptAnswers, selectedAnswers, transformedAnswersMap } = processAnswers({
          answers,
          defaultAnswers,
          modalPrompts,
        });

        setNarrativeTitle(data.name);
        setTransformedAnswersMap(transformedAnswersMap);
        setNonPromptAnswers(nonPromptAnswers);
        setSelectedAnswers(selectedAnswers);
        setSearchPrompts(hierarchySearchPrompts);
        setSameLevelPrompts(sameLevelPrompts);
        setModalPrompts(modalPrompts);
      } catch (error) {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
        console.error('Error fetching Narrative prompts:', error);
        toast('Failed to load prompts.', { status: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchNarrative();

    return () => abortControllerRef.current?.abort();
  }, [dynamicTopicIdsSignature, flowId, normalizePrompt, route?.id, shouldApplyPromptGrouping]);

  useEffect(() => {
    if (!modalPrompts.length) return;

    const matchingPrompt = getAllModalPrompts(modalPrompts).find(
      (prompt) =>
        prompt?.id === selectedPrompt?.id || prompt?.promptKey === selectedPrompt?.promptKey
    );
    const nextActivePrompt =
      matchingPrompt?.groupedPrompts?.length > 0
        ? matchingPrompt
        : modalPrompts.find((prompt) =>
            prompt?.groupedPrompts?.some(
              (groupedPrompt) =>
                groupedPrompt?.id === selectedPrompt?.id ||
                groupedPrompt?.promptKey === selectedPrompt?.promptKey
            )
          ) || matchingPrompt;

    setActivePrompt(nextActivePrompt || modalPrompts[0] || null);
  }, [modalPrompts, selectedPrompt?.id, selectedPrompt?.promptKey]);

  const handleBuildNarrative = useCallback(async () => {
    setBuildError(null);
    setIsBuilding(true);
    try {
      let promptAnswers = transformPromptAnswers(Object.values(transformedAnswersMap).flat());
      promptAnswers = { ...promptAnswers, ...nonPromptAnswers };

      const narrative = await createNarrative({
        flowId,
        id: route?.id,
        promptAnswers,
        title: narrativeTitle,
      });

      if (!narrative?.flowId) {
        setBuildError('There was an error building your Narrative.');
        setIsBuilding(false);
        return;
      }
      if (typeof onNarrativeBuilt === 'function') {
        onNarrativeBuilt(narrative.flowId);
      } else {
        navigate(`/dashboard/narrative/${narrative.flowId}`);
        setIsModalOpen(false);
      }
      setIsBuilding(false);
    } catch (error) {
      const promptErrors = error?.response?.data?.['Prompt Errors'] ?? [];
      const errorMessage = promptErrors[0] || 'There was an error building your Narrative';
      setBuildError(errorMessage);

      console.error(errorMessage, error);
      setIsBuilding(false);
    }
  }, [
    flowId,
    narrativeTitle,
    navigate,
    nonPromptAnswers,
    onNarrativeBuilt,
    route?.id,
    setIsModalOpen,
    transformedAnswersMap,
  ]);

  const dateRangeValue = transformedAnswersMap?.date_range?.[0]?.values?.date_range;

  const isBuildDisabled = useMemo(() => {
    if (modalPrompts.length === 0) return true;

    const requiredPrompts = getAllModalPrompts(modalPrompts)
      .filter((prompt) => prompt.isRequired)
      .map((filteredPrompt) => filteredPrompt.promptKey);

    return !requiredPrompts.every((promptKey) => {
      if (promptKey === 'date_range') {
        return Array.isArray(dateRangeValue) && dateRangeValue.length === 2;
      }

      return Array.isArray(transformedAnswersMap?.[promptKey])
        ? transformedAnswersMap[promptKey].length > 0
        : Boolean(transformedAnswersMap?.[promptKey]);
    });
  }, [dateRangeValue, modalPrompts, transformedAnswersMap]);

  return (
    <PreBuildModal
      activePrompt={activePrompt}
      buildError={buildError}
      getPillTooltip={getPromptPillTooltip}
      getPillValue={getPromptPillValue}
      handleBuild={handleBuildNarrative}
      headerIcon={getNarrativeIcon(route?.path)}
      isBuildDisabled={isBuildDisabled}
      isBuilding={isBuilding}
      isLoading={isLoading}
      isModalOpen
      name={narrativeTitle}
      prompts={modalPrompts}
      routeId={route?.id}
      routeLabel={`${normalizedRouteName} Narrative`}
      sameLevelPrompts={sameLevelPrompts}
      searchPrompts={searchPrompts}
      selectedAnswers={selectedAnswers}
      setActivePrompt={setActivePrompt}
      setIsModalOpen={setIsModalOpen}
      setName={setNarrativeTitle}
      setSelectedAnswers={setSelectedAnswers}
      setTransformedAnswersMap={setTransformedAnswersMap}
      transformedAnswersMap={transformedAnswersMap}
      transformPromptSelections={transformPromptSelections}
      type="Narrative"
    />
  );
};

ModalBuildNarrative.propTypes = {
  flowId: PropTypes.number,
  onNarrativeBuilt: PropTypes.func,
  route: PropTypes.object.isRequired,
  selectedPrompt: PropTypes.object,
  setIsModalOpen: PropTypes.func.isRequired,
  topicIds: PropTypes.array,
};

export default ModalBuildNarrative;
